/**
 * The local Markdown parser — the BLOCK half.
 *
 * ---------------------------------------------------------------------------
 * WHY A PARSER AND NOT A LIBRARY
 * ---------------------------------------------------------------------------
 * The console has no Markdown dependency and is not going to get one for this: the
 * assistant's answers are a small, known subset of Markdown, and a dependency that
 * can execute what a model wrote is a dependency nobody audits.
 *
 * This half is PURE DATA. It turns source text into a list of blocks and knows
 * nothing about React, so it can be tested without a renderer. The rendering half
 * is `markdown.tsx`, and it is a straight mapping from block to element.
 *
 * ---------------------------------------------------------------------------
 * THE SUPPORTED SUBSET
 * ---------------------------------------------------------------------------
 * Headings `#` to `###`, paragraphs, fenced code blocks, bullet and numbered
 * lists, GFM pipe tables, and horizontal rules. Everything else — images, nested
 * lists, block quotes, setext headings — is left as text. A subset that renders
 * honestly beats a subset that renders wrongly.
 */
import type { MarkdownBlock } from '@/modules/assistant/parts/markdown-types';

/** `#` to `###`. A deeper heading is not a heading here. */
const HEADING = /^(#{1,3})\s+(.+?)(?:\s+#+)?\s*$/;

/** A fence, with the info string that names the language. */
const FENCE = /^```\s*([A-Za-z0-9_+#-]*)\s*$/;

/** Three or more of the same rule character, on their own line. */
const RULE = /^ {0,3}([-*_])[ \t]*(?:\1[ \t]*){2,}$/;

/** A bullet item. */
const BULLET = /^\s*[-*+]\s+(.*)$/;

/** A numbered item. */
const ORDERED = /^\s*\d+[.)]\s+(.*)$/;

/** The delimiter row of a GFM pipe table. */
const TABLE_DELIMITER = /^\s*\|?\s*:?-{1,}:?\s*(?:\|\s*:?-{1,}:?\s*)*\|?\s*$/;

/** A block and the index of the line after it. */
type ReadBlock = { block: MarkdownBlock; next: number };

/**
 * Parses Markdown source into blocks.
 *
 * Soft line breaks inside a paragraph are joined with a space, which is what a
 * reader expects from a paragraph and what keeps a model's hard-wrapped answer
 * from being rendered as one line per source line.
 */
export function parseMarkdownBlocks(source: string): MarkdownBlock[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: MarkdownBlock[] = [];
  let index = 0;

  while (index < lines.length) {
    if ((lines[index] ?? '').trim() === '') {
      index += 1;
      continue;
    }
    const read = readBlock(lines, index);
    blocks.push(read.block);
    index = read.next;
  }

  return blocks;
}

/** Reads the block that starts at `index`. */
function readBlock(lines: string[], index: number): ReadBlock {
  const line = lines[index] ?? '';

  if (FENCE.test(line.trim())) return collectFencedCode(lines, index);
  if (RULE.test(line)) return { block: { kind: 'rule' }, next: index + 1 };

  const heading = matchHeading(line);
  if (heading !== null) return { block: heading, next: index + 1 };

  if (isTableStart(lines, index)) return collectTable(lines, index);
  if (BULLET.test(line) || ORDERED.test(line)) return collectList(lines, index);

  return collectParagraph(lines, index);
}

/** A heading, or null when the line is not one. */
function matchHeading(line: string): MarkdownBlock | null {
  const match = HEADING.exec(line);
  if (match === null) return null;

  const hashes = match[1] ?? '';
  const level = hashes.length === 1 ? 1 : hashes.length === 2 ? 2 : 3;
  return { kind: 'heading', level, text: match[2] ?? '' };
}

/** True when the line and the one after it form a pipe table's header. */
function isTableStart(lines: string[], index: number): boolean {
  const header = lines[index] ?? '';
  const delimiter = lines[index + 1] ?? '';
  if (!header.includes('|')) return false;
  if (!TABLE_DELIMITER.test(delimiter)) return false;
  return splitRow(header).length === splitRow(delimiter).length;
}

/** Splits a table row into its cells. */
function splitRow(line: string): string[] {
  const withoutEdges = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return withoutEdges.split('|').map((cell) => cell.trim());
}

/** Collects a fenced code block, up to its closing fence. */
function collectFencedCode(lines: string[], index: number): ReadBlock {
  const language = FENCE.exec((lines[index] ?? '').trim())?.[1] ?? null;
  const body: string[] = [];
  let cursor = index + 1;

  while (cursor < lines.length) {
    const line = lines[cursor] ?? '';
    if (line.trim().startsWith('```')) {
      cursor += 1;
      break;
    }
    body.push(line);
    cursor += 1;
  }

  return {
    block: { kind: 'code', language: language === '' ? null : language, text: body.join('\n') },
    next: cursor,
  };
}

/** Collects a run of list items of ONE kind. */
function collectList(lines: string[], index: number): ReadBlock {
  const ordered = ORDERED.test(lines[index] ?? '');
  const items: string[] = [];
  let cursor = index;

  while (cursor < lines.length) {
    const match = ordered ? ORDERED.exec(lines[cursor] ?? '') : BULLET.exec(lines[cursor] ?? '');
    if (match === null) break;
    items.push((match[1] ?? '').trim());
    cursor += 1;
  }

  return { block: { kind: 'list', ordered, items }, next: cursor };
}

/** Collects a table's body rows. */
function collectTable(lines: string[], index: number): ReadBlock {
  const header = splitRow(lines[index] ?? '');
  const rows: string[][] = [];
  let cursor = index + 2;

  while (cursor < lines.length) {
    const line = lines[cursor] ?? '';
    if (line.trim() === '' || !line.includes('|')) break;
    rows.push(splitRow(line));
    cursor += 1;
  }

  return { block: { kind: 'table', header, rows }, next: cursor };
}

/** Collects the lines of a paragraph. */
function collectParagraph(lines: string[], index: number): ReadBlock {
  const text: string[] = [];
  let cursor = index;

  while (cursor < lines.length) {
    const line = lines[cursor] ?? '';
    if (line.trim() === '' || startsNewBlock(lines, cursor)) break;
    text.push(line.trim());
    cursor += 1;
  }

  return { block: { kind: 'paragraph', text: text.join(' ') }, next: cursor };
}

/** True when the line at `index` begins a block, and so ends a paragraph. */
function startsNewBlock(lines: string[], index: number): boolean {
  const line = lines[index] ?? '';
  if (FENCE.test(line.trim())) return true;
  if (RULE.test(line)) return true;
  if (matchHeading(line) !== null) return true;
  if (isTableStart(lines, index)) return true;
  return BULLET.test(line) || ORDERED.test(line);
}
